import { FormField } from "@/components/controls/Form/Form";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { SingleSelectProps } from "@/components/controls/SingleSelect";
import { ArrayFieldType } from "@/entities/forms/types";
import {
  commonRules,
  validateField,
  ValidationConfig
} from "@/utils/validation";
import {
  parseFileFieldValue,
  serializeFileFieldValue
} from "@/utils/entityFieldsUtils/fileFieldUtils";
import { UseFormWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isValidPhoneNumber } from "libphonenumber-js";
import metadata from "libphonenumber-js/metadata.min.json";

const CALLING_CODES = new Set(Object.keys(metadata.country_calling_codes));

// A value that is at most a dial code ("+44 ", what picking a country alone
// writes) holds no number yet. Digits after the "+" that are not exactly a
// dial code ("+415" is +41 and a 5) are already a number.
export const hasPhoneNumber = (value: unknown): boolean => {
  if (typeof value !== "string") return false;
  const match = /^\s*(?:\+(\d*))?\s*$/.exec(value);
  return !match || (!!match[1] && !CALLING_CODES.has(match[1]));
};

// A number must lead with its dial code to be valid: without one there is no
// country to read it in. True for an empty value or a lone dial code, since
// holding no number is the presence check's to judge, not this one's.
export const isPhoneNumberValid = (value: unknown): boolean =>
  !hasPhoneNumber(value) || isValidPhoneNumber(value as string);

// The phone value to persist for an edit, or null when nothing should be
// written: a lone dial code means "no number" (it clears an optional field
// and never empties a required one), and an unchanged value is not a write.
export const getPhoneValueToSave = (
  nextValue: unknown,
  currentValue: string,
  required: boolean
): string | null => {
  const valueToSave = hasPhoneNumber(nextValue) ? (nextValue as string) : "";
  if (valueToSave === currentValue) return null;
  if (valueToSave === "" && required) return null;
  return valueToSave;
};

export const getFormDefaultValues = (fields: FormFieldNewType[]) => {
  return fields.reduce(
    (acc, field) => {
      // Handle array default values
      if ("isSerializedFileField" in field && field.isSerializedFileField) {
        // This attachments field stores a JSON string, not an array. The array
        // branch below wraps a non-array default in one, and the field then
        // decodes ["[...]"] to nothing: an existing file disappears from the
        // form and saving writes the empty list back over it.
        acc[field.name] = field.defaultValue ?? serializeFileFieldValue([]);
      } else if (
        (Object.values(ArrayFieldType) as string[]).includes(field.type)
      ) {
        acc[field.name] = field.defaultValue
          ? Array.isArray(field.defaultValue)
            ? field.defaultValue
            : [field.defaultValue]
          : [];
      } else if (field.type === "checkbox") {
        // `|| ""` below turns a false default into an empty string, which
        // reaches the API as "" instead of false. Keep the boolean.
        acc[field.name] = field.defaultValue ?? "";
      } else if (field.type === "singleSelect") {
        const singleSelectProps = field.fieldActionProps as SingleSelectProps;
        if (singleSelectProps.treatNullAsValue && field.defaultValue === null) {
          acc[field.name] = null;
        } else {
          acc[field.name] = field.defaultValue || "";
        }
      } else {
        acc[field.name] = field.defaultValue || "";
      }
      return acc;
    },
    {} as Record<string, any>
  );
};

export const getFieldValidation = (
  field: FormField | FormFieldNewType,
  watch: UseFormWatch<Record<string, any>>
) => {
  const { t } = useTranslation();

  const validation: any = {};

  validation.validate = (value: any) => {
    // Get validation config - FormField has validation property, FormFieldNewType doesn't
    const fieldValidation =
      "validation" in field ? field.validation : undefined;

    const config: ValidationConfig = {
      required: field.required,
      ...fieldValidation
    };

    if (field.type === "multiSelect") {
      if (field.required && (!Array.isArray(value) || value.length === 0)) {
        return t("requiredField");
      }
    } else if (field.type === "attachments") {
      // The stored value is a JSON string, so an empty field reads as "[]" —
      // truthy to the generic required rule. Parse before deciding.
      if (field.required && parseFileFieldValue(value).length === 0) {
        return t("requiredField");
      }
    } else if (field.type === "multiEmail") {
      if (field.required && (!Array.isArray(value) || value.length === 0)) {
        return t("requiredField");
      }
      config.customRules = [commonRules.multiEmail(fieldValidation?.message)];
    } else if (field.type === "email") {
      if (fieldValidation?.customRules) {
        config.customRules = fieldValidation.customRules;
      } else {
        config.customRules = [
          commonRules.email(
            fieldValidation?.message || t("validation.invalidEmail")
          )
        ];
      }
    } else if (field.type === "phone") {
      // A bare dial code (picked from the dropdown, no number typed) counts as
      // empty.
      if (field.required && !hasPhoneNumber(value)) {
        return t("requiredField");
      }
      if (!isPhoneNumberValid(value)) {
        return t("validation.invalidPhone");
      }
    } else if (field.type === "number") {
      // Use existing validation rules or fallback to default number validation
      if (fieldValidation?.customRules) {
        config.customRules = fieldValidation.customRules;
      } else {
        config.customRules = [
          commonRules.number({
            min: fieldValidation?.min,
            max: fieldValidation?.max,
            message: fieldValidation?.message || t("validation.invalidNumber")
          })
        ];
      }
    } else if (
      field.type === "text" &&
      "fieldActionProps" in field &&
      (field.fieldActionProps as any)?.type === "url"
    ) {
      if (fieldValidation?.customRules) {
        config.customRules = fieldValidation.customRules;
      } else {
        config.customRules = [
          {
            validate: (value: string) => {
              if (!value) return true;
              try {
                const newUrl = new URL(value);
                return (
                  newUrl.protocol === "http:" || newUrl.protocol === "https:"
                );
              } catch (err) {
                return false;
              }
            },
            message: fieldValidation?.message || t("validation.invalidUrl")
          }
        ];
      }
    }

    const error = validateField(value, config, watch());
    return error || true;
  };

  return validation;
};
